import React from 'react';
import { InputNumber, Select, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import CommercialQuantityEditor from '../orders/components/CommercialQuantityEditor';
import { formatUnit, normalizeUnitCode } from '../../utils/units';
import { formatConversionValue } from '../../utils/dimensionConversion';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../utils/polishNumberInput';
import { COMMERCIAL_UNITS } from '../../utils/commercialQuantity';
import {
  WAREHOUSE_QUANTITY_MIN,
  WAREHOUSE_QUANTITY_PRECISION,
  roundWarehouseQuantity,
} from '../../utils/warehouseQuantity';
import {
  WarehouseQuantityLine,
  WarehouseQuantityPatch,
  buildWarehouseQuantityPatch,
  buildWarehouseQuantityValue,
  getWarehouseLineUnit,
  supportsWarehouseConversion,
} from './warehouseQuantityConversion';

interface WarehouseQuantityFieldProps {
  line: WarehouseQuantityLine;
  onChange: (patch: WarehouseQuantityPatch) => void;
  maxQuantity: number;
  maxConvertedQuantity: number;
  disabled?: boolean;
  /**
   * Render the unit as an operator-editable Select.
   *
   * TRUE for free-text, non-dimensional lines which use the inline selector.
   * Dimensional catalog lines select their input unit inside the shared modal.
   */
  unitEditable?: boolean;
  /** wz:conversion.noDimensions / pz:conversion.noDimensions — the caller owns its namespace. */
  noDimensionsLabel: string;
  unitLabel: string;
  pricingQuantity?: number | null;
  pricingUnit?: string | null;
  /** Keep supplier-order-traced PZ stock quantity fixed while allowing a commercial unit conversion. */
  physicalQuantityLocked?: boolean;
  /** Only WZ creation uses zero as an instruction to omit a not-yet-issued line. */
  onZero?: () => void;
}

// Active product scope is intentionally limited to the three mutually
// convertible dimensional views. Historical scalar codes stay displayable but
// cannot be selected for a new or edited document line.
const FREE_TEXT_UNIT_CODES = [...COMMERCIAL_UNITS];

/**
 * The quantity control on a WZ / PZ document line.
 *
 * Replaces the old "Ilość + Jednostka + Przelicznik" trio of loose controls with
 * the quantity plus the same "Przelicznik jednostek" modal the order lines
 * already use. That modal is bidirectional: the operator can enter pieces,
 * area or volume and select either remaining view. The old per-line Select
 * only ever computed one direction and showed the result as small grey text.
 *
 * Dimensional lines choose their transaction unit inside that same modal.
 * A catalog product never supplies or locks a unit; only a traced PZ amount
 * can be fixed to the supplier line's already-agreed physical quantity.
 */
const WarehouseQuantityField: React.FC<WarehouseQuantityFieldProps> = ({
  line,
  onChange,
  maxQuantity,
  maxConvertedQuantity,
  disabled = false,
  unitEditable = false,
  noDimensionsLabel,
  unitLabel,
  pricingQuantity,
  pricingUnit,
  physicalQuantityLocked = false,
  onZero,
}) => {
  const { t, i18n } = useTranslation(['orders']);
  const lineUnit = getWarehouseLineUnit(line);
  const quantity = Number(line.quantity) || 0;

  const showUnitPicker = unitEditable && !line.productVariantId && supportsWarehouseConversion(line);
  const unitPicker = showUnitPicker ? (
    <label style={{ display: 'block' }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{unitLabel}</Typography.Text>
      <Select
      aria-label={unitLabel}
      value={normalizeUnitCode(line.unit) ?? 'pcs'}
      onChange={(unit) => onChange({
        quantity: roundWarehouseQuantity(line.quantity),
        // A unit switch invalidates any conversion derived from the old one.
        conversionUnit: null,
        unit,
      })}
      disabled={disabled}
      options={FREE_TEXT_UNIT_CODES.map((unit) => ({
        value: unit,
        label: formatUnit(unit, i18n.language),
      }))}
        style={{ width: '100%' }}
      />
    </label>
  ) : null;

  // Legacy weight/length rows have no dimensional conversion. They remain
  // readable, but the active editor only offers szt./m²/m³.
  if (!supportsWarehouseConversion(line)) {
    return (
      <>
        <InputNumber
          value={quantity}
          onChange={(next) => {
            // Clearing an InputNumber briefly yields null while the operator
            // types a replacement value. Only an explicit numeric zero means
            // "omit this line"; an empty edit must not delete it by accident.
            if (onZero && next !== null && Number(next) === 0) {
              onZero();
              return;
            }
            onChange({
              quantity: roundWarehouseQuantity(next),
              conversionUnit: null,
              unit: lineUnit,
            });
          }}
          disabled
          min={onZero ? 0 : WAREHOUSE_QUANTITY_MIN}
          max={maxQuantity}
          step={WAREHOUSE_QUANTITY_MIN}
          precision={WAREHOUSE_QUANTITY_PRECISION}
          addonAfter={showUnitPicker ? undefined : formatUnit(line.unit, i18n.language)}
          style={{ width: '100%' }}
          {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
        />
        {unitPicker}
        <Typography.Text type="warning" style={{ display: 'block', fontSize: 12 }}>
          {t('orders:unitConversion.legacyUnitReadOnly')}
        </Typography.Text>
      </>
    );
  }

  const conversionLabel = line.conversionUnit
    ? formatConversionValue(line.conversionUnit, { ...line, unit: lineUnit }, quantity)
    : '';

  return (
    <>
      <CommercialQuantityEditor
        value={buildWarehouseQuantityValue({ ...line, pricingQuantity, pricingUnit })}
        onChange={(next) => onChange(buildWarehouseQuantityPatch(next))}
        maxPhysicalQuantity={maxQuantity}
        maxPricingQuantity={maxConvertedQuantity}
        physicalUnit={lineUnit}
        lockPhysicalQuantity={physicalQuantityLocked}
        pricingConversionUnits={COMMERCIAL_UNITS}
        disabled={disabled}
        compact
        allowZero={Boolean(onZero)}
        onZero={onZero}
        width={line.width}
        length={line.length}
        height={line.height}
        thickness={line.thickness}
      />
      {unitPicker}
      {line.conversionUnit && !conversionLabel && (
        <Typography.Text type="warning" style={{ fontSize: 12 }}>
          {noDimensionsLabel}
        </Typography.Text>
      )}
    </>
  );
};

export default WarehouseQuantityField;
